<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';

import AccountPane from './AccountPane.vue';
import BillingPane from './BillingPane.vue';
import LegalPane from './LegalPane.vue';
import ProfileIcon from './ProfileIcon.vue';
import PurchasesPane from './PurchasesPane.vue';
import WithdrawalsPane from './WithdrawalsPane.vue';
import BrandLogo from '@/ui/BrandLogo.vue';
import { useAccount } from '@/state/account';
import { fetchSiteInfo, getSiteInfo } from '@/core/preview/preview';

/**
 * Profile, account, billing, purchases and the legal documents.
 *
 * **A full-screen overlay rather than a seventh garage tab**, and the reason is
 * measured rather than aesthetic: the title bar's chrome already runs to about
 * 1,600px on a 1,440px screen, the tab strip takes its own row below 1,700px, and
 * `GarageScreen`'s own notes record what happened the last time a tab was added to
 * that budget — the currencies landed over the Shop tab and *Sign out* wrapped onto
 * two lines. A button in the account cluster costs one icon's width; a tab costs a
 * label plus a gap, on the one row that must never give way.
 *
 * It is also the right shape for what this is. The garage tabs are *places you fit
 * a tank*; this is a document about the person, read occasionally and at length,
 * with a table in it. Full-screen gives the billing table room to be a table.
 *
 * **It is opened from two screens** — the garage's title bar, and the verification
 * gate, where it is the only other door: somebody who registered with a typo in
 * their address has to be able to close the account they cannot use. That is why
 * `AccountController` carries no `EmailVerifiedGuard`, and why this component makes
 * no assumption about being reached from a working garage.
 *
 * **The rail is four panes and each loads on demand, once.** A player who never
 * presses Billing never fetches an invoice; see `state/account.ts` for why `loaded`
 * is per pane rather than one flag.
 *
 * **The rail and the document are one capped, centred composition**, not a rail
 * pinned to the viewport's left edge with a document flowing out of it. See
 * `.frame` in the stylesheet below for the measurement; the short version is that
 * this screen is composed the way the storefront is, because they are read at the
 * same window sizes and are the same product.
 */
type Pane = 'account' | 'billing' | 'purchases' | 'withdrawals' | 'legal';

const withdrawalsEnabled = ref(getSiteInfo()?.features.withdrawals ?? false);
if (!getSiteInfo()) {
  void fetchSiteInfo().then((info) => {
    withdrawalsEnabled.value = info.features.withdrawals;
  });
}

const PANES = computed(() => [
  { id: 'account' as const, label: 'Account', blurb: 'Name, email, language, password' },
  { id: 'billing' as const, label: 'Billing', blurb: 'Invoices and receipts' },
  { id: 'purchases' as const, label: 'Purchases', blurb: 'Diamonds and credits' },
  ...(withdrawalsEnabled.value ? [{ id: 'withdrawals' as const, label: 'Withdrawals', blurb: 'Withdraw your balance' }] : []),
  { id: 'legal' as const, label: 'Legal', blurb: 'Terms, privacy, cookies' },
]);

const emit = defineEmits<{ close: [] }>();

const pane = ref<Pane>('account');
const account = useAccount();

/**
 * The Account pane is what the overlay opens on, so its data is fetched here
 * rather than in that component — the header shows the call sign and the
 * verification badge, and it must not render them blank for a frame while the
 * pane it is wrapping does its own fetch.
 */
onMounted(() => {
  account.loadAccount().catch(() => {});
  window.addEventListener('keydown', onKey);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKey);
  // Not a full `clear()`: what has been fetched is still true, and dropping it
  // would refetch four lists the next time somebody looked at their own email.
  // Only the transient status goes, so a stale error does not greet the next open.
  account.dismiss();
});

/**
 * Escape closes it.
 *
 * A key rather than only the button, because that is what a full-screen overlay
 * over a game is expected to answer to — and it is safe here in a way it is not in
 * the arena, where Escape belongs to pointer lock and the menu. This overlay is
 * never mounted during a battle; see `App.vue`.
 */
function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
  }
}

/** The account is gone, so there is nothing behind this to return to. */
function onDeleted(): void {
  emit('close');
}
</script>

<template>
  <div class="overlay" data-test="profile-overlay">
    <header class="bar">
      <BrandLogo class="brand" />
      <div class="who">
        <span class="who-label">Signed in as</span>
        <span class="who-name" data-test="profile-header-callsign">
          {{ account.profile.value?.callSign ?? '—' }}
        </span>
        <span class="who-mail">{{ account.profile.value?.email ?? '' }}</span>
      </div>
      <button type="button" class="close" data-test="profile-close" @click="emit('close')">
        Close
        <span class="esc">Esc</span>
      </button>
    </header>

    <div class="body">
      <div class="frame">
        <nav class="rail" role="tablist" aria-label="Profile sections">
          <button
            v-for="entry in PANES"
            :key="entry.id"
            type="button"
            role="tab"
            class="rail-item"
            :class="{ active: pane === entry.id }"
            :aria-selected="pane === entry.id"
            :data-test="`profile-tab-${entry.id}`"
            @click="pane = entry.id"
          >
            <ProfileIcon :name="entry.id" />
            <span class="rail-text">
              <span class="rail-label">{{ entry.label }}</span>
              <span class="rail-blurb">{{ entry.blurb }}</span>
            </span>
          </button>
        </nav>

        <main class="content">
          <p v-if="account.error.value" class="status error" data-test="profile-error">
            {{ account.error.value }}
          </p>

          <AccountPane v-if="pane === 'account'" @deleted="onDeleted" />
          <BillingPane v-else-if="pane === 'billing'" />
          <PurchasesPane v-else-if="pane === 'purchases'" />
          <WithdrawalsPane v-else-if="pane === 'withdrawals'" />
          <LegalPane v-else />
        </main>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  /*
   * 50, not the 40 every other dialog in this app uses, and the exception is
   * earned: those are centred boxes over a garage, and this is a full-screen
   * document that owns the viewport. `MusicControl` is also at 40 and is mounted
   * *after* this in `App.vue`, so at an equal z-index it painted on top —
   * measured, it put its mute pill across the Account pane's currency field.
   * Below `LoadingScreen`'s 60, which must always win.
   *
   * That pill is retired and `MusicControl` draws only an arena flash now, so
   * this particular collision cannot recur; 50 stays for the ordering it states.
   * Nothing was lost by going over the music control in the first place: `M`
   * mutes from any screen because the key listener lives in `audio/music.ts`
   * rather than in that component — which is exactly why it was put there — and
   * the control itself is a row in the settings window.
   */
  z-index: 50;
  display: flex;
  flex-direction: column;
  /*
   * Translucent, at the storefront's own figure and for the storefront's own
   * reason: `GarageScreen`'s `.garage:not(.immersive) .body` is
   * `rgba(11, 14, 18, 0.74)`, and what ghosts through it is what says the garage
   * never went away. This was an opaque radial wash, which made the overlay read
   * as having *replaced* the app rather than as a document laid over it.
   *
   * The radial layer over the wash is tint, not opacity: it keeps the warm
   * top-left lift the opaque version had without closing the wash back up.
   *
   * The panels inside stay **opaque** (`pane.css`'s `.block`), so nothing that
   * has to be read sits on a transparency — only the ground between them does.
   *
   * ── The blur, and why the rule against one does not reach this scrim ────────
   *
   * This carried `no backdrop-filter` for the reason every scrim in this app
   * carries it: a blur is a per-frame cost over a live WebGL canvas, and the
   * result screen's own notes record dropping one. **That premise is false
   * here.** `App.vue` renders `GarageScreen` and the arena `<canvas>` as two
   * branches of one `v-else-if` chain, and this overlay is opened only by
   * `GarageScreen`'s `@profile` — so when it is up, the arena is not mounted at
   * all. What is underneath is the garage's hangar-room preview: one tank on a
   * turntable, which the player can switch off entirely in settings, and not a
   * thirty-hertz battle.
   *
   * So the figure is chosen for the room it is actually in. 6px is enough to
   * separate the document from the floor behind it and not enough to be the
   * reason a mid-range laptop drops the garage's frame rate; the wash still does
   * most of the work, and the blur only stops the storefront's high-contrast
   * item cards competing with the pane text sitting over them.
   *
   * If this overlay is ever mounted over `phase === 'playing'`, this is the rule
   * that has to be read again before it is — the blur, not the wash, is the part
   * that would need to go.
   */
  background:
    radial-gradient(circle at 20% 0%, rgba(34, 45, 59, 0.55) 0%, rgba(34, 45, 59, 0) 58%),
    rgba(11, 14, 18, 0.74);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

/*
 * The header is full-width chrome over a centred document, which is exactly the
 * arrangement the garage already has — a title bar spanning the window with the
 * storefront capped and centred beneath it. Capping this bar to the frame's own
 * width would leave the brand floating in the middle of a lintel, which is not
 * how any other header in this product behaves.
 *
 * The amber hairline under the steel one is the emails' own device (an accent
 * rule under a stencilled wordmark) and the only warm pixel in the chrome — it
 * costs nothing and stops the bar reading as a generic app toolbar.
 */
.bar {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex: none;
  padding: 0.85rem 1.35rem;
  border-bottom: 1px solid var(--steel-700);
  box-shadow: 0 1px 0 rgba(232, 114, 44, 0.16);
  background: rgba(11, 13, 17, 0.78);
}

.brand {
  --brand-logo-height: 1.15rem;
  flex: none;
  /*
   * A seam between the wordmark and the person it belongs to, so the two do not
   * read as one run of text. Padding rather than a margin on `.who`, so the rule
   * is the brand's own trailing edge.
   *
   * `align-self: stretch` is what makes it a rule rather than a tick:
   * `BrandLogo`'s root is an `inline-flex` sized to its image, so a bordered
   * flex item is 1.15rem tall beside a three-line block. Stretched, the item is
   * the bar's own height and the logo stays centred in it by that component's
   * own `align-items`.
   */
  align-self: stretch;
  padding-right: 1.15rem;
  border-right: 1px solid var(--steel-700);
}

.who {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  line-height: 1.2;
}

/*
 * The eyebrow. The call sign alone was a bold word next to a wordmark, which
 * reads as a second brand; naming what it is costs one 0.58rem line and turns
 * the block into a statement about the reader. Same eyebrow-over-figure shape
 * the shop's cards use for their prices.
 */
.who-label {
  color: var(--steel-400);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.who-name {
  overflow: hidden;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.who-mail {
  overflow: hidden;
  color: var(--steel-400);
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*
 * `ConfirmPurchase`'s `.ghost` exactly — a transparent 1px `--steel-600` outline
 * at 3px radius in `--steel-200`, which is this game's secondary button and is
 * what the dialogs a player already meets are wearing. It shipped as a filled
 * `--steel-800` plate, which is the *rail chip's* vocabulary: a chip is a thing
 * you select and a button is a thing you press, and dressing the two the same
 * was the error.
 */
.close {
  display: flex;
  gap: 0.55rem;
  align-items: center;
  flex: none;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--steel-600);
  border-radius: 3px;
  background: transparent;
  color: var(--steel-200);
  font-size: 0.82rem;
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease;
}

.close:hover,
.close:focus-visible {
  border-color: var(--steel-400);
  color: #fff;
}

.esc {
  padding: 0.1rem 0.35rem;
  border: 1px solid var(--steel-600);
  border-radius: 2px;
  color: var(--steel-400);
  font-size: 0.6rem;
  letter-spacing: 0.08em;
}

/*
 * ─── The composition ─────────────────────────────────────────────────────────
 *
 * `.body` is the one scroller and `.frame` is what it scrolls: the rail and the
 * document as a single capped, centred pair, which is `DiamondShop`'s `.store`
 * and the Cosmetics tab's own container.
 *
 * **The pair, not the document.** Centring the content while the rail stayed
 * pinned to the viewport's left edge would put the document visibly right of
 * centre and leave the rail exactly where the complaint found it. What this
 * replaced was worse still: the rail was a fixed 14rem column and `.content` was
 * an uncapped `flex: 1`, so on anything past a laptop the form and the billing
 * table hugged the left edge with the rest of the window empty beside them.
 *
 * The rail can be sticky *because* of this arrangement and not before it — the
 * rail used to be its own full-height scroller beside `.content`, where sticky
 * has nothing to stick within.
 */
/*
 * `scroll`, not `auto`, and it is a **bug fix rather than a preference**.
 *
 * The four panes are wildly different heights — Account overflows and Legal's
 * three-row list does not — so with `auto` the scrollbar appeared and vanished
 * as sections were changed. This screen's scrollbar is a styled one occupying
 * real width (10px, measured in `style.css`), and the frame inside is
 * *centred*: gaining or losing that gutter moves every centred thing by 5px, so
 * the document and the rail jumped sideways on every click of the rail. Always
 * reserving the gutter makes the composition's width a constant.
 *
 * `scrollbar-gutter: stable` is the other way to say this and is deliberately
 * not used: `style.css` warns that it also reserves a channel on scrollers that
 * are *not* overflowing, which would be a layout change on every other panel in
 * the game. This declaration is local to this one element.
 */
.body {
  /*
   * The scroller's top padding, as a property rather than a literal.
   *
   * **Nothing outside this rule reads it any more**, and that is the point of
   * saying so here. It was introduced so the sticky rail's inset could be "the
   * same number as the padding" — a rule that turned out to be wrong (the inset
   * is measured from the content box, so it counted the padding twice; see
   * `.rail`). The property stays because it keeps the two breakpoints' figures
   * named and paired with their own shorthand, which is worth having on its own.
   *
   * **Do not reconnect it to `.rail`.** That link is the bug.
   */
  --body-pad-top: 1.6rem;
  flex: 1;
  min-height: 0;
  padding: var(--body-pad-top) 1.5rem 3rem;
  overflow-y: scroll;
}

/*
 * The cap.
 *
 * The floor is `min(58rem, 100%)` — the storefront's own idiom, so a window too
 * narrow for 58rem gives way to its own width instead of forcing a horizontal
 * scrollbar. The ceiling is a rem figure rather than the store's `100%`, and that
 * is the one deliberate departure: a storefront's subject is pictures, which are
 * worth scaling to a 27" monitor, and this screen's subject is a form and a
 * table, which are not. 74vw between the two, so the composition grows with the
 * window until it stops being a document.
 *
 * Measured: ~1,066px on a 1,440 laptop (leaving ~11% of background each side,
 * the store's own proportion), and pinned at 1,216px from 1,644px up — so a
 * 2,560px monitor shows the same document, centred, rather than a form field a
 * metre wide.
 */
.frame {
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;
  width: 100%;
  max-width: clamp(min(58rem, 100%), 74vw, 76rem);
  margin-inline: auto;
}

/*
 * The rail. Sticky, for the storefront's reason: the Account pane is four blocks
 * tall and a document read at length should not make somebody scroll back up to
 * change section.
 *
 * No container border and no container padding — the chips carry their own
 * plate, which is what the shop's rail does and is the same move as adopting its
 * buttons. A border down the rail's right edge would draw a seam through the
 * middle of a composition whose whole point is that it is one block.
 */
/*
 * **A grid rather than a column of flex items, so the four chips are one size.**
 * Their blurbs are one line or two depending on the words in them ("Name, email,
 * language, password" against "Diamonds and credits"), so as flex items they
 * were four different heights — a ragged column, and a menu whose items differ
 * in size reads as four unrelated controls rather than one set of four. The
 * width was already equal (a column stretches its items); `grid-auto-rows: 1fr`
 * is what makes the *height* equal, sized to the tallest, without anybody
 * stating a figure that a re-worded blurb would then falsify.
 */
/*
 * ─── `top: 0`, and it is the whole of a bug this comment got wrong twice ─────
 *
 * The rail is sticky, and the position it sticks to has to be **the same place
 * it stands when it is not sticking** — otherwise changing section moves the
 * menu, which is what was reported: *"first and last item when selected moves
 * the menu lower than when the middle items are selected."*
 *
 * **The sticky inset is measured from the scrollport's content box, not its
 * padding box.** That is the fact both previous attempts missed. `.body` is the
 * scroller with 25.6px of top padding, and `.frame` is its first in-flow child
 * with no block margin — so the rail's static position *is* the content-box
 * top. An inset of `0` therefore asks for exactly where it already is, and the
 * two states coincide.
 *
 * What this replaced was `top: var(--body-pad-top)`, i.e. 25.6px, on the stated
 * rule that "a sticky inset must equal the scroller's own padding". That rule is
 * wrong in this direction: it **counts the padding twice**, so the constraint
 * lands 25.6px *below* the static position and the rail is pushed down by the
 * full inset the moment it has anywhere to go.
 *
 * **Which is why the jump followed the pane rather than the scroll.** A sticky
 * box cannot be offset past its containing block, and the containing block is
 * `.frame`, whose height is the taller of the rail and the pane. Measured at
 * 844x390 with the rail 189.5px tall:
 *
 * | pane | content height | rail y |
 * |---|---|---|
 * | Account | 1932 | **123.5** |
 * | Billing | 189.5 | 97.9 |
 * | Purchases | 189.5 | 97.9 |
 * | Legal | 273.5 | **123.5** |
 *
 * Billing and Purchases are each a single block *shorter than the rail*, so
 * `.frame` is exactly rail-height and there is no room to shift — the wrong
 * inset was unreachable and the rail sat right. Account and Legal are taller, so
 * it applied in full. Exactly the two the report names, and nothing to do with
 * how far anything was scrolled.
 *
 * With `top: 0` all four read 97.9, and scrolling 400px still pins the rail at
 * 97.9 — so it sticks, level with the pane's first block, in both states.
 *
 * Do not "restore" the padding here. If this is ever re-derived, measure
 * `rail.getBoundingClientRect().y` on a short pane *and* a tall one and require
 * the two to be equal; that comparison is the property, and it is the one both
 * earlier readings of this rule failed while sounding correct.
 */
.rail {
  position: sticky;
  top: 0;
  display: grid;
  grid-auto-rows: 1fr;
  flex: none;
  gap: 4px;
  width: 10rem;
}

/*
 * A chip: a bordered plate on `--steel-800`, uppercase tracked type, a glyph and
 * a blurb. The glyph and the blurb are the difference from the storefront's bare
 * category label, and an earned one — a shop rail filters a catalogue, and this
 * is a document's contents, so saying what is inside each section is the rail's
 * job rather than clutter on it.
 *
 * **The selected chip is filled and bordered all the way round**, and that is a
 * reversal of what shipped first: it wore a 3px amber bar down its left edge,
 * borrowed from the storefront's rail. The bar is a *mark beside* a control, and
 * the thing being marked here is a whole section rather than a row in a list —
 * so the honest statement is that the chip itself is lit, which is what
 * `SettingsPanel`'s chosen segment does (it fills with the accent outright) and
 * what `DailyQuestsWindow`'s ready quest does (border on all four sides).
 *
 * A tint rather than the settings panel's solid accent, because that control is
 * one uppercase word and this one carries a caption underneath: a solid amber
 * plate would need the blurb inverted to dark type at 0.64rem, which is less
 * legible than what it replaced. Border, fill and text all move together, so the
 * lit chip is unmistakable without any of them shouting alone.
 *
 * **Every border is 1px in every state**, which is what keeps selecting a
 * section from moving the three below it — the property the old 3px-transparent
 * left border was written to preserve, now true because there is nothing that
 * changes width at all.
 */
.rail-item {
  display: flex;
  gap: 0.55rem;
  align-items: center;
  padding: 0.7rem 0.75rem;
  border: 1px solid var(--steel-700);
  border-radius: 3px;
  background: var(--steel-800);
  color: var(--steel-400);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
}

.rail-item:hover:not(.active) {
  border-color: var(--steel-600);
  background: var(--steel-700);
  color: var(--steel-200);
}

.rail-item.active {
  border-color: var(--accent);
  background: linear-gradient(
    180deg,
    rgba(232, 114, 44, 0.26) 0%,
    rgba(232, 114, 44, 0.13) 100%
  );
  color: #fff;
}

/* The glyph takes the chip's colour, so it brightens with the label rather than
   needing a palette of its own — except while the section is live, where it goes
   amber to match the plate it sits on. */
.rail-item.active :deep(.pf-icon) {
  color: var(--accent);
}

/* Lifted out of `--steel-400` on the lit chip: the caption has to stay a step
   behind its label, but a step behind on a warm plate is not the same value as
   a step behind on steel. */
.rail-item.active .rail-blurb {
  color: #e2c3ac;
}

.rail-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.rail-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

/* Deliberately not `currentColor`: the blurb is a caption under the label, so it
   stays a step back even on the chip that is lit. */
.rail-blurb {
  color: var(--steel-400);
  font-size: 0.64rem;
  letter-spacing: 0.02em;
  line-height: 1.3;
}

/*
 * ─── Short: the caption goes, the column stays ───────────────────────────────
 *
 * **The one place in this file that does key off `(max-height: 520px)`**, and it
 * is the convention's own argument rather than an exception to the width rule
 * above. That rule is right that a landscape phone wide enough for the rail
 * should keep it — an 844px-wide handset is 52.75rem and stays in the column
 * layout deliberately. What it does not buy is height, and the blurb is what
 * makes a chip two lines instead of one: four two-line chips against a 390px
 * viewport is most of the screen spent on navigation before the pane it
 * navigates to has been drawn.
 *
 * So the caption is what gives way here, exactly as it does in the strip, and
 * for the reason stated there — a glyph and a word are still four distinct
 * silhouettes. `grid-auto-rows: 1fr` keeps the four one size without this
 * having to say what that size now is.
 */
@media (max-height: 520px) {
  .rail-blurb {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .close,
  .rail-item {
    transition: none;
  }
}

/*
 * The document column, and a **named size container** so a pane can compose
 * against the width it actually has rather than against the viewport — the rail
 * and the cap both stand between the two, and a media query cannot see either.
 * `AccountPane` is the reader; see its two-up grid.
 *
 * Named rather than anonymous so a container added inside a pane later cannot
 * silently capture a query written against this one.
 */
.content {
  flex: 1;
  min-width: 0;
  align-self: stretch;
  container: profile-pane / inline-size;
}

.status {
  margin: 0 0 1.1rem;
  padding: 0.65rem 0.85rem;
  border-left: 3px solid;
  border-radius: 0 3px 3px 0;
  font-size: 0.82rem;
}

.status.error {
  border-color: #d9483b;
  background: rgba(217, 72, 59, 0.1);
  color: #ff8478;
}

/*
 * ─── Narrow: the rail turns into a strip ─────────────────────────────────────
 *
 * **A width, deliberately, where the rest of this codebase keys off
 * `(max-height: 520px)`.** That convention exists because the garage's shortage
 * is height — a landscape phone is short, not narrow. *Whether the rail is a
 * column or a strip* is the opposite question and a genuine width fact: the
 * billing table is six columns, and below some window width the rail's column is
 * what stops them fitting. Height has nothing to do with it, and a landscape
 * phone wide enough for the rail is better off keeping it, because a horizontal
 * strip spends the one dimension such a phone is short of.
 *
 * The height query above is not a second opinion about that — it decides
 * something else entirely (whether a chip is one line or two) and leaves the
 * layout this rule picked alone.
 *
 * The number is derived rather than inherited: 10rem of rail + 1.25rem of gap +
 * `.body`'s 3rem of side padding + the table's own floor (~524px of six columns
 * inside 2.5rem of block padding) ≈ 792px. 50rem. It was 46rem against a 14rem
 * rail, and moving the rail without moving this would have left the stated
 * reason false.
 */
@media (max-width: 50rem) {
  .bar {
    padding: 0.7rem 0.9rem;
    gap: 0.75rem;
  }

  .brand {
    padding-right: 0.75rem;
  }

  /* Retuning the padding retunes the property as well as the shorthand, so the
     two never disagree. Nothing else reads it — see the base rule. */
  .body {
    --body-pad-top: 0.9rem;
    padding: var(--body-pad-top) 0.9rem 3rem;
  }

  .frame {
    flex-direction: column;
    gap: 0.6rem;
  }

  /*
   * Not sticky, and it is the storefront's own reversal for the storefront's own
   * reason: a pinned strip over ~300px of content height is a sixth of the
   * screen spent on navigation for the whole session. It scrolls away with the
   * document and scrolls sideways itself if the four chips outgrow the window.
   */
  /*
   * Still a grid, now flowing across: `minmax(max-content, 1fr)` gives the four
   * chips one width when the row has the space and each its own content width
   * when it does not, at which point `overflow-x` scrolls the strip. Equal
   * columns with no minimum would truncate "Purchases" to fit a phone, and a
   * truncated label is worse than a scroll.
   */
  .rail {
    position: static;
    grid-auto-flow: column;
    grid-auto-columns: minmax(max-content, 1fr);
    grid-auto-rows: auto;
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.15rem;
    gap: 0.3rem;
  }

  /* The fill and the full border carry the selection here too, so nothing about
     the mark has to be restated for the strip — which is the dividend of it
     being the chip that is lit rather than an edge beside it. */
  .rail-item {
    min-height: 44px;
    justify-content: center;
  }

  /* The blurb is what gives way, and the glyph is what makes that affordable —
     four chips reduced to a word each are still four distinct silhouettes. */
  .rail-blurb {
    display: none;
  }

  /*
   * `flex: 1` is a *height* once the frame turns into a column, and this is a
   * size container: `min-height: auto` is the only thing that would stop it
   * flexing to nothing, which is a rule to depend on by accident rather than on
   * purpose. `flex: none` states the height is its content's; `align-self:
   * stretch` on the base rule states the width is the frame's — the pair the
   * storefront's own pane carries for exactly this layout.
   */
  .content {
    flex: none;
  }
}

/*
 * Touch targets. Asked of the device rather than plumbed through as a prop —
 * `SettingsPanel`'s idiom, in the form CSS can ask it — so a desktop's density
 * is untouched. The rail chips already clear 44px in the strip layout above;
 * this is the column layout, where they were 38.
 */
@media (hover: none) and (pointer: coarse) {
  .rail-item,
  .close {
    min-height: 44px;
  }
}
</style>
